import { firestore } from 'core/config/firebase'
import { Collection } from 'core/constants/collection'
import {
  dateFormat,
  dayAndDateFormat,
  ReportType,
  WorkLocation,
  WorkRequestStatus,
  WorkRequestType,
  YMDFormat,
} from 'core/constants/enum'
import {
  GazettedHoliday,
  Holiday,
  Report,
  TimesheetLog,
  User,
  UserActivityUpdated,
  UserLeave,
  UserTimesheetLog,
  UserWorkRequest,
} from 'core/service'
import {
  milliSecondsToHMFormat,
  milliSecondsToHours,
  timestampToHMFormat,
} from 'features/dashboard/utils/dashboard'
import { getBreakDuration } from 'features/members/utils/members'
import { LocationSummary } from 'features/reports/components/LocationReportDisplay'
import {
  calculateNoTFoundShifts,
  getUniqueObjectsWithinArray,
  getUpdatedReports,
  UUID,
} from 'features/reports/utils'
import {
  and,
  collection,
  doc,
  getDocs,
  or,
  orderBy,
  query,
  where,
  writeBatch,
} from 'firebase/firestore'
import { chunk, filter, round, sortBy } from 'lodash'
import moment from 'moment'
import { extendMoment } from 'moment-range'
import { toast } from 'react-toastify'
import ReportService from './index'
import { getTimesheetLogs } from '../thunks/reports'

// @ts-ignore
const Moment_Range = extendMoment(moment)

const getFormattedDateRange = (from: number, to: number) => {
  const filterRange = Moment_Range.range(moment(from), moment(to))
  return Array.from(filterRange.by('day')).map(date => date.format(YMDFormat))
}

const getKeyBasedTotalDuration = (
  userShifts: Report[],
  key: keyof Report,
): number =>
  userShifts.reduce((sum: number, obj: Report) => {
    const value: string | number | boolean = obj[key] ?? 0
    if (typeof value === 'number') {
      return sum + value
    }
    return sum
  }, 0)

const getFilteredBasedLeaveRequests = (
  requests: UserWorkRequest[],
  start: number,
  to: number,
): UserWorkRequest[] => {
  const startFilter = moment(start)
  const toFilter = moment(to)
  return filter(requests, request => {
    const fromDate = moment(request.fromDate)
    const toDate = moment(request.toDate)
    return (
      fromDate.isBetween(startFilter, toFilter, null, '[]') ||
      toDate.isBetween(startFilter, toFilter, null, '[]')
    )
  })
}

const getWeekendReports = (from: number, to: number) => {
  const filterRange = Moment_Range.range(moment(from), moment(to))
  const weekends = Array.from(filterRange.by('day')).filter(date => {
    const dayOfWeek = date.day()
    return dayOfWeek === 6 || dayOfWeek === 0
  })
  return weekends.map(date => ({
    date: date.format(dayAndDateFormat),
    isWeekend: true,
    shift: date.day() === 6 ? 'Saturday' : 'Sunday',
  }))
}

const getWeekLabel = (
  startDate: moment.Moment,
  endDate: moment.Moment,
): string => `${startDate.format('MMMM DD')} – ${endDate.format('MMMM DD')}`

const groupReportsByWeek = (
  reports: Report[],
): Map<
  string,
  { reports: Report[]; startDate: moment.Moment; endDate: moment.Moment }
> => {
  const weekMap = new Map()
  // Skip reports without dates (like summary rows)
  const reportsWithDates = reports.filter(report => report.date)
  // Sort by date
  const sortedReports = reportsWithDates.sort(
    (a, b) =>
      moment(a.date, dayAndDateFormat).valueOf() -
      moment(b.date, dayAndDateFormat).valueOf(),
  )
  let currentWeekReports: Report[] = []
  let weekStart: moment.Moment | null = null
  let weekEnd: moment.Moment | null = null
  sortedReports.forEach((report, idx) => {
    const reportDate = moment(report.date, dayAndDateFormat)
    // If this is the first report or a new week
    if (!weekStart) {
      weekStart = reportDate.clone()
      // If not Monday, week starts from this date
    }
    // Week ends on Sunday or last report
    const isSunday = reportDate.isoWeekday() === 7
    const isLast = idx === sortedReports.length - 1
    currentWeekReports.push(report)
    if (isSunday || isLast) {
      weekEnd = reportDate.clone()
      const weekLabel = getWeekLabel(weekStart, weekEnd)
      weekMap.set(weekLabel, {
        reports: currentWeekReports,
        startDate: weekStart,
        endDate: weekEnd,
      })
      // Reset for next week
      currentWeekReports = []
      weekStart = null
      weekEnd = null
    }
  })
  return weekMap
}

const calculateWeeklyTotals = (
  weeklyReports: Report[],
  userName: string,
  weekLabel: string,
  employeeNo: string = '', // Added employeeNo parameter
  startDate?: moment.Moment,
  endDate?: moment.Moment,
): Report => {
  const wfoCount = weeklyReports.filter(
    report => report.wfh === WorkLocation.Office,
  ).length
  const wfhCount = weeklyReports.filter(
    report => report.wfh === WorkLocation.Home,
  ).length
  const leaveCount = weeklyReports.filter(report => report.isLeave).length

  const WorkDuration = getKeyBasedTotalDuration(weeklyReports, 'workDuration')
  const TotalBreak = getKeyBasedTotalDuration(weeklyReports, 'breakDuration')
  const TotalDuration = getKeyBasedTotalDuration(weeklyReports, 'totalDuration')
  const TotalDevLogHours = getKeyBasedTotalDuration(
    weeklyReports,
    'devlogHours',
  )
  const TotalLeaveHours = getKeyBasedTotalDuration(weeklyReports, 'leaveHours')
  const TotalOutageHours = getKeyBasedTotalDuration(
    weeklyReports,
    'outageHours',
  )
  const TotalDeviationHrs = getKeyBasedTotalDuration(
    weeklyReports,
    'deviationHours',
  )
  const TotalTrainingHours = getKeyBasedTotalDuration(
    weeklyReports,
    'trainingHours',
  )
  const TotalBillableHours = getKeyBasedTotalDuration(
    weeklyReports,
    'billableHours',
  )
  const TotalNonBillableHours = getKeyBasedTotalDuration(
    weeklyReports,
    'nonBillableHours',
  )

  // Format date range for display in the Date column (May 01 - May 06 format)
  const dateRange =
    startDate && endDate
      ? `${startDate.format('MMM DD')} - ${endDate.format('MMM DD')}`
      : ''

  return {
    date: dateRange, // Use the date range in the Date column
    name: userName,
    employeeNo, // Add employee number
    shift: `WFO: ${wfoCount}, WFH: ${wfhCount}, Leave: ${leaveCount}`, // Combined Shift data
    wfh: '', // Empty Location column as we're combining with Shift
    isWeeklyTotal: true,
    workDuration: milliSecondsToHMFormat(WorkDuration),
    workDurationInHrs: WorkDuration ?? 0,
    breakDuration: milliSecondsToHMFormat(TotalBreak) ?? '',
    totalDuration: milliSecondsToHMFormat(TotalDuration) ?? '',
    devlogHours: round(TotalDevLogHours, 2),
    leaveHours: round(TotalLeaveHours, 2),
    trainingHours: round(TotalTrainingHours, 2),
    billableHours: round(TotalBillableHours, 2),
    nonBillableHours: round(TotalNonBillableHours, 2),
    outageHours: round(TotalOutageHours, 2),
    netDevlogHrs: round(TotalDevLogHours - TotalLeaveHours, 2),
    deviationHours: round(TotalDeviationHrs, 2),
  }
}

class Reports implements ReportService {
  async getMultipleUsersShifts(
    uids: string[],
    from: number,
    to: number,
    workLocation: string,
  ): Promise<Report[]> {
    const collRef = collection(firestore, Collection.Shifts)

    const shifts = [] as UserActivityUpdated[]
    const commonFromClause = where('shiftDate', '>=', from)
    const commonEndClause = where('shiftDate', '<=', to)
    const orderClause = orderBy('shiftDate', 'asc')
    if (uids.length < 11) {
      const userClause = where('userId', 'in', uids)
      const queryRef = query(
        collRef,
        userClause,
        commonFromClause,
        commonEndClause,
        orderClause,
      )
      const queryWithLocation = query(
        collRef,
        userClause,
        commonFromClause,
        commonEndClause,
        where('workLocation', '==', workLocation),
        orderClause,
      )
      const collSnap = await getDocs(
        workLocation !== 'All' ? queryWithLocation : queryRef,
      )
      const shiftsData = collSnap.docs.map(
        document =>
          ({ id: document.id, ...document.data() } as UserActivityUpdated),
      )
      shifts.push(...shiftsData)
    } else {
      const batchSize = 10
      const batchedUids = chunk(uids, batchSize)
      for (const batchUids of batchedUids) {
        const queryRef = query(
          collRef,
          commonFromClause,
          commonEndClause,
          where('userId', 'in', batchUids),
          orderClause,
        )
        const queryWithLocation = query(
          collRef,
          commonFromClause,
          commonEndClause,
          where('userId', 'in', batchUids),
          where('workLocation', '==', workLocation),
          orderClause,
        )
        const collSnap = await getDocs(
          workLocation !== 'All' ? queryWithLocation : queryRef,
        )
        const shiftsData = collSnap.docs.map(
          document =>
            ({ id: document.id, ...document.data() } as UserActivityUpdated),
        )
        shiftsData?.length && shifts.push(...shiftsData)
      }
    }

    const shiftReports = [] as Report[]

    shifts.forEach(activity => {
      let workDuration: string | number = '',
        workDurationInHrs = 0,
        totalDuration: string | number = '',
        shift = ''
      if (activity.start === 0) {
        workDuration = totalDuration = 'Work not Started'
        shift = 'Not Started'
      } else if (activity.start !== 0 && activity.end === 0) {
        workDuration = totalDuration = 'Work in Progress'
        shift = `${timestampToHMFormat(activity?.start ?? 0)} - Present`
      } else {
        const workInMilliseconds = (activity?.end ?? 0) - (activity?.start ?? 0)
        const breakInMilliseconds = getBreakDuration(activity?.intervals ?? [])
        workDuration = workInMilliseconds - breakInMilliseconds
        workDurationInHrs = workInMilliseconds - breakInMilliseconds
        totalDuration = workInMilliseconds
        shift = `${timestampToHMFormat(
          activity?.start ?? 0,
        )} - ${timestampToHMFormat(activity.end ?? 0)}`
      }
      shiftReports.push({
        date: moment(activity?.shiftDate).format(dayAndDateFormat),
        shift,
        wfh: activity.workLocation,
        workDuration,
        workDurationInHrs,
        breakDuration: getBreakDuration(activity?.intervals ?? []),
        totalDuration,
        shiftId: activity.id,
        userId: activity.userId,
      })
    })
    return shiftReports
  }

  async getMultipleUsersTimesheetLogs(
    uids: string[],
    from: number,
    to: number,
  ): Promise<Report[]> {
    const collRef = collection(firestore, Collection.TimesheetLogs)
    const timesheetLogs = [] as UserTimesheetLog[]

    const commonFromClause = where('logDate', '>=', from)
    const commonEndClause = where('logDate', '<=', to)
    if (uids.length < 11) {
      const userClause = where('userId', 'in', uids)
      const queryRef = query(
        collRef,
        userClause,
        commonFromClause,
        commonEndClause,
      )
      const collSnap = await getDocs(queryRef)
      const logsData = collSnap?.docs?.map(
        document =>
          ({ id: document.id, ...document.data() } as UserTimesheetLog),
      )
      logsData?.length && timesheetLogs.push(...logsData)
    } else {
      const batchSize = 10
      const batchedUids = chunk(uids, batchSize)
      for (const batchUids of batchedUids) {
        const queryRef = query(
          collRef,
          commonFromClause,
          commonEndClause,
          where('userId', 'in', batchUids),
        )
        const collSnap = await getDocs(queryRef)
        const logsData = collSnap.docs.map(
          document =>
            ({ id: document.id, ...document.data() } as UserTimesheetLog),
        )
        logsData?.length && timesheetLogs.push(...logsData)
      }
    }

    return timesheetLogs?.map(
      log =>
        ({
          date: moment(log?.logDate).format(dayAndDateFormat),
          devlogHours: round(log.totalHours, 2),
          leaveHours: round(log.leaveHours, 2),
          outageHours: round(log.outageHours, 2),
          trainingHours: round(log.trainingHours ?? 0, 2),
          billableHours: round(log.billableHours ?? 0, 2),
          nonBillableHours: round(log.nonBillableHours ?? 0, 2),
          logId: log.id,
          userId: log.userId,
        } as Report),
    )
  }

  async getGazettedHolidaysReports(
    from: number,
    to: number,
  ): Promise<Report[]> {
    const filterRange = Moment_Range.range(moment(from), moment(to))
    const dateRange = getFormattedDateRange(from, to)
    const gazettedHolidays = [] as GazettedHoliday[]
    const holidayColRef = collection(firestore, Collection.GazettedHolidays)
    if (dateRange?.length < 16) {
      const holidayQueryRef = query(
        holidayColRef,
        or(
          where('startDate', 'in', dateRange),
          where('endDate', 'in', dateRange),
        ),
      )
      const holidaysSnap = await getDocs(holidayQueryRef)
      const holidaysData = holidaysSnap.docs.map(
        holidayDoc =>
          ({
            id: holidayDoc.id,
            ...holidayDoc.data(),
          } as GazettedHoliday),
      )
      holidaysData?.length && gazettedHolidays.push(...holidaysData)
    } else {
      const batchSize = 15
      const batchedFilterDates = chunk(dateRange, batchSize)
      for (const batchFilterDates of batchedFilterDates) {
        const holidayQueryRef = query(
          holidayColRef,
          or(
            where('startDate', 'in', batchFilterDates),
            where('endDate', 'in', batchFilterDates),
          ),
        )
        const holidayCollSnap = await getDocs(holidayQueryRef)
        const holidaysData = holidayCollSnap?.docs.map(reqDocument => ({
          id: reqDocument.id,
          ...reqDocument.data(),
        })) as GazettedHoliday[]
        holidaysData?.length && gazettedHolidays.push(...holidaysData)
      }
    }

    const uniqueHolidays = getUniqueObjectsWithinArray(gazettedHolidays, 'id')
    const holidaysDates = [] as Holiday[]
    uniqueHolidays.forEach(holiday => {
      const range = Moment_Range.range(
        moment(holiday.startDate),
        moment(holiday.endDate),
      )
      const dates = Array.from(range.by('day')).map(date => ({
        date: date.format(YMDFormat),
        title: holiday.holidayTitle,
      }))
      holidaysDates.push(...dates)
    })

    const filteredHolidays = holidaysDates.filter(({ date }) =>
      filterRange.contains(moment(date)),
    )

    return filteredHolidays.map(({ date, title }) => ({
      date: moment(date).format(dayAndDateFormat),
      isHoliday: true,
      shift: title,
    }))
  }

  async getUserLeavesReports(
    uids: string[],
    from: number,
    to: number,
  ): Promise<Report[]> {
    const filterRange = Moment_Range.range(moment(from), moment(to))
    const collRef = collection(firestore, Collection.WorkRequests)
    const leaveRequests = [] as UserWorkRequest[]
    if (uids?.length < 11) {
      const reqQueryRef = query(
        collRef,
        and(
          where('userId', 'in', uids),
          where('type', '==', WorkRequestType.Leave),
          or(
            where('status', '==', WorkRequestStatus.Approved),
            where('status', '==', WorkRequestStatus.Open),
          ),
        ),
      )
      const collSnap = await getDocs(reqQueryRef)
      const data = collSnap?.docs.map(reqDoc => ({
        id: reqDoc.id,
        ...reqDoc.data(),
      })) as UserWorkRequest[]
      data?.length && leaveRequests.push(...data)
    } else {
      const batchSize = 10
      const batchedUids = chunk(uids, batchSize)
      for (const batchUids of batchedUids) {
        const reqQueryRef = query(
          collRef,
          and(
            where('userId', 'in', batchUids),
            where('type', '==', WorkRequestType.Leave),
            or(
              where('status', '==', WorkRequestStatus.Approved),
              where('status', '==', WorkRequestStatus.Open),
            ),
          ),
        )
        const collSnap = await getDocs(reqQueryRef)
        const data = collSnap?.docs.map(reqDoc => ({
          id: reqDoc.id,
          ...reqDoc.data(),
        })) as UserWorkRequest[]
        data?.length && leaveRequests.push(...data)
      }
    }

    const leaves = getFilteredBasedLeaveRequests(leaveRequests, from, to)
    const uniqueRequests = getUniqueObjectsWithinArray(leaves, 'id')
    const userLeaves = [] as UserLeave[]
    uniqueRequests.forEach(req => {
      const range = Moment_Range.range(moment(req.fromDate), moment(req.toDate))
      const leaveDatesPerUser = Array.from(range.by('day')).map(date => ({
        date: date.format(dateFormat),
        leaveType: req.leaveType,
        status: req.status,
        userId: req.userId,
      }))
      userLeaves.push(...leaveDatesPerUser)
    })
    const filteredLeaves = userLeaves?.filter(({ date }) =>
      filterRange.contains(moment(date)),
    )
    return filteredLeaves.map(({ date, status, userId }) => ({
      date: moment(date).format(dayAndDateFormat),
      isLeave: true,
      isLeavePending: status === WorkRequestStatus.Open,
      shift: `${
        status === WorkRequestStatus.Open
          ? 'Leave - Pending For Approval'
          : 'On Leave'
      } `,
      userId,
    }))
  }
  async getReports(
    uids: string[],
    fromDate: number,
    toDate: number,
    reportType: string,
    workLocation: string,
  ): Promise<Array<Report>> {
    const userCollRef = query(
      collection(firestore, 'users'),
      where('isActive', '==', true),
      orderBy('name', 'asc'),
    )
    const usersSnap = await getDocs(userCollRef)
    const users = usersSnap.docs
      .filter(user => uids.indexOf(user.id) > -1)
      .map(user => ({ uid: user.id, ...user.data() } as User))

    const filterRange = Moment_Range.range(moment(fromDate), moment(toDate))
    const weekOffDates = getWeekendReports(fromDate, toDate)
    const holidaysReports = await this.getGazettedHolidaysReports(
      fromDate,
      toDate,
    )
    const shifts = await this.getMultipleUsersShifts(
      uids,
      fromDate,
      toDate,
      workLocation,
    )
    const leaves = await this.getUserLeavesReports(uids, fromDate, toDate)
    const timesheetLogs = await this.getMultipleUsersTimesheetLogs(
      uids,
      fromDate,
      toDate,
    )
    let userReports = [] as Report[]

    users.forEach(user => {
      let userReport = [] as Report[]
      const userShifts = shifts
        .filter(shift => shift.userId === user.uid)
        .map(shift => ({
          name: user.name,
          employeeNo: user.employeeNo || '',
          ...shift,
        }))

      const userLeaves = leaves
        .filter(leave => leave.userId === user.uid)
        .map(leave => ({
          name: user.name,
          employeeNo: user.employeeNo || '',
          ...leave,
        }))

      const userTimesheetLogs = timesheetLogs
        .filter(log => log.userId === user.uid)
        .map(log => ({
          name: user.name,
          employeeNo: user.employeeNo || '',
          ...log,
        }))

      userShifts.forEach(shift => {
        userTimesheetLogs.forEach(devlog => {
          if (shift.date === devlog.date) {
            shift.devlogHours = devlog.devlogHours
            shift.leaveHours = devlog.leaveHours
            shift.outageHours = devlog.outageHours
            shift.trainingHours = devlog.trainingHours ?? 0
            shift.billableHours = devlog.billableHours ?? 0
            shift.nonBillableHours = devlog.nonBillableHours ?? 0
            shift.netDevlogHrs =
              (devlog?.devlogHours ?? 0) - (devlog?.leaveHours ?? 0)
            shift.deviationHours = round(
              (devlog?.devlogHours ?? 0) -
                (devlog?.leaveHours ?? 0) -
                milliSecondsToHours(shift?.workDurationInHrs ?? 0),
              2,
            )
          }
        })
      })

      userLeaves.forEach(leave => {
        userTimesheetLogs.forEach(devlog => {
          if (leave.date === devlog.date) {
            leave.devlogHours = devlog.devlogHours
            leave.leaveHours = devlog.leaveHours
            leave.outageHours = devlog.outageHours
            leave.trainingHours = devlog.trainingHours ?? 0
            leave.billableHours = devlog.billableHours ?? 0
            leave.nonBillableHours = devlog.nonBillableHours ?? 0
            leave.netDevlogHrs =
              (devlog?.devlogHours ?? 0) - (devlog?.leaveHours ?? 0)
            leave.deviationHours = round(
              (devlog?.devlogHours ?? 0) - (devlog?.leaveHours ?? 0),
              2,
            )
          }
        })
      })
      userReport.push(...userShifts)

      if (reportType === ReportType.User) {
        const datesToExclude = userReport.map(obj =>
          moment(obj.date).format(dayAndDateFormat),
        )
        const updatedWeekendDates = weekOffDates
          .filter(({ date }) => !datesToExclude.includes(date))
          .map(weekend => ({
            name: user.name,
            employeeNo: user.employeeNo || '',
            ...weekend,
          }))
        const updatedHolidaysReports = holidaysReports
          .filter(
            ({ date }) =>
              ![
                ...datesToExclude,
                ...updatedWeekendDates.map(weekend => weekend.date),
              ].includes(date),
          )
          .map(holiday => ({
            name: user.name,
            employeeNo: user.employeeNo || '',
            ...holiday,
          }))
        const updatedLeaveReports = userLeaves.filter(
          ({ date }) => !datesToExclude.includes(date),
        )

        const noShifts: Report[] = calculateNoTFoundShifts(
          [
            ...updatedWeekendDates,
            ...updatedHolidaysReports,
            ...userReport,
            ...updatedLeaveReports,
          ],
          filterRange,
          user.name,
          user.employeeNo || '',
        )

        noShifts.forEach(noShift => {
          userTimesheetLogs.forEach(devlog => {
            if (noShift.date === devlog.date) {
              noShift.devlogHours = devlog.devlogHours
              noShift.leaveHours = devlog.leaveHours
              noShift.outageHours = devlog.outageHours
              noShift.trainingHours = devlog.trainingHours ?? 0
              noShift.billableHours = devlog.billableHours ?? 0
              noShift.nonBillableHours = devlog.nonBillableHours ?? 0
              noShift.netDevlogHrs =
                (devlog?.devlogHours ?? 0) - (devlog?.leaveHours ?? 0)
              noShift.deviationHours = round(
                (devlog?.devlogHours ?? 0) - (devlog?.leaveHours ?? 0),
                2,
              )
            }
          })
        })

        // Combine all report entries for this user
        const combinedUserReports = sortBy(
          [
            ...noShifts,
            ...userReport,
            ...updatedLeaveReports,
            ...updatedWeekendDates,
            ...updatedHolidaysReports,
          ],
          obj => moment(obj.date),
        )

        // Group reports by week
        const weeklyReports = groupReportsByWeek(combinedUserReports)

        // Create a new array with weekly totals inserted
        let enhancedUserReport: Report[] = []
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-const
        let currentWeekDate = ''

        // Sort the weeks chronologically
        const sortedWeeks = Array.from(weeklyReports.keys()).sort(
          (a, b) =>
            moment(a.split(' – ')[0], 'MMMM DD').valueOf() -
            moment(b.split(' – ')[0], 'MMMM DD').valueOf(),
        )

        // Add reports with weekly summaries
        sortedWeeks.forEach(weekLabel => {
          const weekInfo = weeklyReports.get(weekLabel)
          if (!weekInfo) return
          const weekData = weekInfo.reports
          // Sort data within the week by date
          const sortedWeekData = sortBy(weekData, obj => {
            const date = moment(obj.date, dayAndDateFormat)
            return date.valueOf()
          })
          // Add data for the week, followed by the weekly total
          enhancedUserReport = [
            ...enhancedUserReport,
            ...sortedWeekData,
            calculateWeeklyTotals(
              weekData,
              user.name,
              weekLabel,
              user.employeeNo || '',
              weekInfo.startDate,
              weekInfo.endDate,
            ),
          ]
        })

        userReport = enhancedUserReport

        // Calculate grand totals
        const wfoCount = userReport.filter(
          report => report.wfh === WorkLocation.Office,
        ).length

        const wfhCount = userReport.filter(
          report => report.wfh === WorkLocation.Home,
        ).length

        const WorkDuration = getKeyBasedTotalDuration(
          userShifts,
          'workDuration',
        )

        const TotalBreak = getKeyBasedTotalDuration(userShifts, 'breakDuration')
        const TotalDuration = getKeyBasedTotalDuration(
          userShifts,
          'totalDuration',
        )
        const TotalDevLogHours = getKeyBasedTotalDuration(
          [...userShifts, ...userLeaves],
          'devlogHours',
        )
        const TotalLeaveHours = getKeyBasedTotalDuration(
          [...userShifts, ...userLeaves],
          'leaveHours',
        )
        const TotalOutageHours = getKeyBasedTotalDuration(
          [...userShifts, ...userLeaves],
          'outageHours',
        )
        const TotalDeviationHrs = getKeyBasedTotalDuration(
          [...userShifts, ...userLeaves],
          'deviationHours',
        )

        userReport.push({
          date: '',
          name: '',
          shift: `WFO Count: ${wfoCount} -- WFH Count: ${wfhCount} -- Leave Count: ${userLeaves.length}`,
          wfh: '',
          workDuration: milliSecondsToHMFormat(WorkDuration),
          workDurationInHrs: WorkDuration ?? 0,
          breakDuration: milliSecondsToHMFormat(TotalBreak) ?? '',
          totalDuration: milliSecondsToHMFormat(TotalDuration) ?? '',
          devlogHours: round(TotalDevLogHours, 2),
          leaveHours: round(TotalLeaveHours, 2),
          trainingHours: round(
            getKeyBasedTotalDuration(
              [...userShifts, ...userLeaves],
              'trainingHours',
            ),
            2,
          ),
          billableHours: round(
            getKeyBasedTotalDuration(
              [...userShifts, ...userLeaves],
              'billableHours',
            ),
            2,
          ),
          nonBillableHours: round(
            getKeyBasedTotalDuration(
              [...userShifts, ...userLeaves],
              'nonBillableHours',
            ),
            2,
          ),
          outageHours: round(TotalOutageHours, 2),
          netDevlogHrs: round(TotalDevLogHours - TotalLeaveHours, 2),
          deviationHours: round(TotalDeviationHrs, 2),
        })
      } else {
        const noShifts: Report[] = calculateNoTFoundShifts(
          [...weekOffDates, ...userLeaves, ...userReport, ...holidaysReports],
          filterRange,
          user.name,
          user.employeeNo || '',
        )
        noShifts.forEach(noShift => {
          userTimesheetLogs.forEach(devlog => {
            if (noShift.date === devlog.date) {
              noShift.devlogHours = devlog.devlogHours
              noShift.leaveHours = devlog.leaveHours
              noShift.outageHours = devlog.outageHours
              noShift.trainingHours = devlog.trainingHours ?? 0
              noShift.billableHours = devlog.billableHours ?? 0
              noShift.nonBillableHours = devlog.nonBillableHours ?? 0
              noShift.netDevlogHrs =
                (devlog?.devlogHours ?? 0) - (devlog?.leaveHours ?? 0)
              noShift.deviationHours = round(
                (devlog?.devlogHours ?? 0) - (devlog?.leaveHours ?? 0),
                2,
              )
            }
          })
        })
        userReport.push(...noShifts, ...userLeaves)
      }
      userReports = userReports.concat(userReport)
    })

    if (reportType === ReportType.Date) {
      const weekends = weekOffDates.map(weekend => weekend.date)
      const updatedHolidaysReports = holidaysReports.filter(
        ({ date }) => !weekends.includes(date),
      )

      userReports = sortBy(
        [...weekOffDates, ...updatedHolidaysReports, ...userReports],
        [obj => moment(obj.date), 'name'],
      )
    }
    return getUpdatedReports(userReports)
  }

  async importTimesheetData(startDate: string, endDate: string): Promise<void> {
    const timesheetData = await getTimesheetLogs(startDate, endDate)
    if (!timesheetData?.length) {
      toast.error('No data found for selected date.')
      return
    }

    const userCollRef = query(
      collection(firestore, 'users'),
      where('isActive', '==', true),
    )
    const usersSnap = await getDocs(userCollRef)
    const users = usersSnap.docs.map(
      row => ({ uid: row.id, ...row.data() } as User),
    )

    const UsersMap = new Map()
    users.forEach(user => {
      UsersMap.set(user.email, user.uid)
    })
    const uids = users.map(user => user.uid)

    const timesheetLogs = []
    const batchedUids = chunk(uids, 10)
    for (const batchUids of batchedUids) {
      const timesheetLogsQuery = query(
        collection(firestore, 'timesheet-logs'),
        where('logDate', '>=', moment(startDate, YMDFormat).valueOf()),
        where('logDate', '<=', moment(endDate, YMDFormat).valueOf()),
        where('userId', 'in', batchUids),
      )
      const timesheetLogsSnap = await getDocs(timesheetLogsQuery)
      const logsData = timesheetLogsSnap.docs.map(
        document =>
          ({
            id: document.id,
            ...document.data(),
          } as TimesheetLog),
      )
      logsData?.length && timesheetLogs.push(...logsData)
    }

    const LogsUserMap = new Map()
    timesheetLogs.forEach(log => {
      LogsUserMap.set(`${log.userId}___${log.logDate}`, log.id)
    })

    const updatedLogsData = timesheetData.map(row => ({
      logDate: new Date(row.TS_date).getTime(),
      outageHours: row.outageHours ?? 0,
      leaveHours: row.leaveHours ?? 0,
      totalHours:
        (row.projectHours ?? 0) +
        (row.outageHours ?? 0) +
        (row.leaveHours ?? 0),
      userEmail: row.employeeEmail,
      trainingHours: row?.trainingHours ?? 0,
      nonBillableHours: row?.nonBillableHours ?? 0,
      billableHours: row?.billableHours ?? 0,
      userId: UsersMap.get(row.employeeEmail) ?? '',
    }))

    const batchSize = 500
    const dataChunks = chunk(updatedLogsData, batchSize)

    const promises = dataChunks.map(dataChunk => {
      const batch = writeBatch(firestore)
      dataChunk.forEach(row => {
        const idToSearchInMap = `${row.userId}___${row.logDate}`
        if (LogsUserMap.get(idToSearchInMap)) {
          const docId = LogsUserMap.get(idToSearchInMap)
          const docRef = doc(firestore, 'timesheet-logs', docId)
          batch.update(docRef, row)
        } else {
          const docRef = doc(firestore, `timesheet-logs`, UUID())
          batch.set(docRef, row)
        }
      })
      return batch.commit()
    })
    await Promise.all(promises)
    toast.success('Data imported successfully.')
  }

  async getLocationReport(
    fromDate: number,
    toDate: number,
  ): Promise<LocationSummary[]> {
    // Fetch all active users
    const userCollRef = query(
      collection(firestore, 'users'),
      where('isActive', '==', true),
      orderBy('name', 'asc'),
    )
    const usersSnap = await getDocs(userCollRef)
    const users = usersSnap.docs.map(
      user => ({ uid: user.id, ...user.data() } as User),
    )

    const filterRange = Moment_Range.range(moment(fromDate), moment(toDate))

    // Format date range for display
    const dateRange = `${moment(fromDate).format('MM/DD/YY')} - ${moment(
      toDate,
    ).format('MM/DD/YY')}`

    // Get weekend dates to exclude
    const weekends = Array.from(filterRange.by('day'))
      .filter(date => date.day() === 0 || date.day() === 6)
      .map(date => date.format(dayAndDateFormat))

    // Get all shifts for the date range
    const allShifts = [] as UserActivityUpdated[]
    const userIds = users.map(user => user.uid)

    // Get shifts in batches
    const batchSize = 10
    const batchedUids = chunk(userIds, batchSize)
    for (const batchUids of batchedUids) {
      const queryRef = query(
        collection(firestore, Collection.Shifts),
        where('shiftDate', '>=', fromDate),
        where('shiftDate', '<=', toDate),
        where('userId', 'in', batchUids),
      )
      const collSnap = await getDocs(queryRef)
      const shiftsData = collSnap.docs.map(
        document =>
          ({ id: document.id, ...document.data() } as UserActivityUpdated),
      )
      allShifts.push(...shiftsData)
    }

    // Get all leaves for the date range
    const validUserIds = userIds.filter((id): id is string => id !== undefined)
    const leaves = await this.getUserLeavesReports(
      validUserIds,
      fromDate,
      toDate,
    )

    // Calculate location counts for each user
    const locationSummaries = users.map(user => {
      // Filter shifts for this user
      const userShifts = allShifts.filter(shift => shift.userId === user.uid)

      // Count work from office days
      const wfoCount = userShifts.filter(
        shift => shift.workLocation === WorkLocation.Office,
      ).length

      // Count work from home days
      const wfhCount = userShifts.filter(
        shift => shift.workLocation === WorkLocation.Home,
      ).length

      // Count leave days (excluding weekends)
      const userLeaves = leaves
        .filter(leave => leave.userId === user.uid)
        .filter(leave => !weekends.includes(leave.date)).length

      // Count workdays in the range (excluding weekends)
      const workdaysInRange = Array.from(filterRange.by('day')).filter(
        date => date.day() !== 0 && date.day() !== 6,
      ).length

      // Calculate leave days (workdays - (wfo + wfh))
      // This counts days with no shift as leave
      const calculatedLeaveCount = Math.max(
        0,
        workdaysInRange - (wfoCount + wfhCount),
      )

      // Prefer actual leave records if available, otherwise use calculated
      const leaveCount = userLeaves || calculatedLeaveCount

      return {
        name: user.name,
        employeeNo: user.employeeNo || '',
        dateRange,
        wfoCount,
        wfhCount,
        leaveCount,
      }
    })

    return locationSummaries.sort((a, b) => a.name.localeCompare(b.name))
  }
}

export const reportService = new Reports()
